import { Alert, Box, Typography, useTheme } from '@mui/material';
import { useContext, useState, useRef, useEffect } from 'react';
import PartnerSignUpForm from '@/components/signup_components/PartnerSignup/PartnerSignUpForm';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { PartnerIntakeProvider } from '@/Contexts/PartnerIntakeContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { UserProps, UserType } from '@/models/User';
import Header from '../components/header/Header';
import { NotFound } from '@/components/NotFound';
import { IntakeResponse } from '@/models/IntakeResponse';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { useNavigate } from 'react-router';
import usePartner from '@/hooks/usePartner';
import { PartnerProps } from '@/models/Partner';
import { Spinner } from '@/components/common/Spinner';
import StickyHeader from '@/components/common/StickyHeader';
import IntakeProgramSelect from '@/components/signup_components/PartnerSignup/common/IntakeProgramSelect';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { ProgramNames } from '@/models/Program';

function NewPartnerIntake() {
  const navigate = useNavigate();
  const { getPartner } = usePartner();
  const { userType, updateUserCurrent } = useContext(UsersContext);
  const { updatePartnerId, updatePartnersInPartnerGroup } = useContext(SelectedPartnerContext);
  const { setAlert } = useContext(SnackbarContext);
  const { allPrograms } = useContext(ConfigContext);

  const [selectedProgramId, setSelectedProgramId] = useState<ProgramNames | null>(null);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const { translatedText } = useContext(LocaleContext);

  useEffect(() => {
    if (scrollRef.current != null) {
      scrollRef.current.scrollTop = 0;
      setShowStickyHeader(false);
    }
  }, []);

  const handleScroll = () => {
    if (scrollRef.current != null) {
      setShowStickyHeader(scrollRef.current.scrollTop > 50);
    }
  };

  const handleSubmit = async (intakeResponse: IntakeResponse) => {
    setAlert({
      severity: 'success',
      message: translatedText.signup.messageSubmissionSuccess,
    });

    updatePartnerId(intakeResponse.partnerId);
    updateUserCurrent((prevValue) => {
      if (prevValue == null) return prevValue;
      return { ...prevValue, currentPartnerId: intakeResponse.partnerId } satisfies UserProps;
    });

    try {
      const partnerData = await getPartner(intakeResponse.partnerId);
      if (updatePartnersInPartnerGroup != null) {
        updatePartnersInPartnerGroup((prevValue: PartnerProps[] | null) => {
          if (prevValue == null) return [partnerData];

          return [...prevValue, partnerData];
        });

        navigate('/');
      }
    } catch {
      setAlert({
        severity: 'error',
        message: translatedText.signup.messageSubmissionError,
      });
    }
  };

  const handleProgramChange = (programId: ProgramNames) => {
    setSelectedProgramId(programId);
  };

  if (userType !== UserType.Applicant) {
    return <NotFound />;
  }

  return (
    translatedText != null && (
      <Box
        display='flex'
        flexDirection='column'
        height='100vh'
        width='100vw'
        sx={{
          overflowX: 'hidden',
        }}
      >
        <Header />

        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          display='flex'
          flexDirection='column'
          alignSelf='center'
          alignItems='center'
          gap={2}
          width='100vw'
          marginBottom={10}
          sx={{
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <StickyHeader
            title={translatedText?.profileMenu?.addAccount ?? ''}
            showStickyHeader={showStickyHeader}
          />

          <Typography variant='h1'>{translatedText?.profileMenu?.addAccount ?? ''}</Typography>
          <Alert severity='info'>
            <Typography variant='subtitle1'>
              {translatedText?.profileMenu?.addAccountTooltip ?? ''}
            </Typography>
          </Alert>

          <Box
            display='flex'
            width='60vw'
            justifyContent='center'
            flexDirection='column'
            gap={3}
            padding={5}
            borderColor={theme.palette.sandShade2.main}
            alignItems='center'
            sx={{
              backgroundColor: theme.palette.sandShade1.main,
            }}
          >
            {(allPrograms?.length ?? 0) > 0 ? (
              <IntakeProgramSelect onChange={handleProgramChange} keepCurrentLocale />
            ) : (
              <Spinner type='circular' />
            )}
            <PartnerIntakeProvider disabledNewPartnerCheckbox={true} programId={selectedProgramId}>
              <PartnerSignUpForm onSubmit={handleSubmit} />
            </PartnerIntakeProvider>
          </Box>
        </Box>
      </Box>
    )
  );
}

export default NewPartnerIntake;
